import React, { useState } from 'react';
import { validateCoupon } from '../services/api';
import { CouponApplication } from '../types';
import { validateCouponCode } from '../utils/validation';
import { theme } from '../config/theme';

interface CouponInputProps {
  amount: number;
  onCouponApplied: (application: CouponApplication) => void;
  onCouponRemoved: () => void;
}

const CouponInput: React.FC<CouponInputProps> = ({ amount, onCouponApplied, onCouponRemoved }) => {
  const [code, setCode] = useState('');
  const [displayCode, setDisplayCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponApplication | null>(null);

  const handleApplyCoupon = async () => {
    if (!code.trim()) {
      setError('Please enter a coupon code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await validateCoupon(code.toUpperCase(), amount);
      setAppliedCoupon(result);
      onCouponApplied(result);
      setCode('');
      setDisplayCode('');
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const upperCode = e.target.value.toUpperCase();
    
    // Validate coupon code format
    const validation = validateCouponCode(upperCode);
    if (!validation.isValid && upperCode.length > 0) {
      setError(validation.error || 'Invalid coupon code format');
    } else {
      setError('');
    }
    
    setCode(validation.sanitized);
    setDisplayCode(validation.sanitized);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    onCouponRemoved();
    setError('');
    setCode('');
    setDisplayCode('');
  };

  if (appliedCoupon) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-md p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-green-800">
              Coupon Applied!
            </div>
            <div className="text-sm text-green-600">
              You saved ${appliedCoupon.discount_amount.toFixed(2)}
            </div>
          </div>
          <button
            onClick={handleRemoveCoupon}
            className="text-green-600 hover:text-green-800 text-sm font-medium"
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={displayCode}
          onChange={handleCodeChange}
          placeholder="Promo Code"
          className="flex-1 sm:flex-2 min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': theme.colors.primary.radio.selected } as React.CSSProperties}
          onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
        />
        <button
          onClick={handleApplyCoupon}
          disabled={loading}
          className="px-4 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 min-w-[80px] sm:min-w-[100px]"
          style={{ backgroundColor: theme.colors.primary[800] }}
          onMouseEnter={(e) => !loading && (e.currentTarget.style.opacity = '0.9')}
          onMouseLeave={(e) => !loading && (e.currentTarget.style.opacity = '1')}
        >
          {loading ? 'Applying...' : 'Apply'}
        </button>
      </div>
      {error && (
        <div className="mt-2 text-sm text-red-600">
          {error}
        </div>
      )}
    </div>
  );
};

export default CouponInput;
